import type { CameraGestureOptions, CameraState, VelocityState, RotationMode } from './types';

// ============================================================================
// CAMERA GESTURE CONTROLLER
// ============================================================================
// Handles touch/pointer/wheel gestures for orbit camera control.
// Framework-agnostic - works with any orbit camera system.
// 
// Usage:
//   const controller = new CameraGestureController({ rotationSpeed: 0.005 });
//   controller.attach(canvas);
//   
//   // In your render loop:
//   controller.update();
//   camera.alpha = controller.state.alpha;
//   camera.beta = controller.state.beta;
//   camera.radius = controller.state.radius;

// ============================================================================
// Constants (defaults)
// ============================================================================

const DEFAULT_OPTIONS: Required<CameraGestureOptions> = {
  disabled: false,
  rotation: 'both',
  zoom: true,
  allowPageScroll: false,
  rotationSpeed: 0.005,
  minZoom: 0.3,
  maxZoom: 3.0,
  velocityEase: 0.4,
  velocityDecay: 0.92,
  velocityThreshold: 0.0001,
  minBeta: 0.3,
  maxBeta: Math.PI - 0.3,
};

// ============================================================================
// Controller
// ============================================================================

export class CameraGestureController {
  private options: Required<CameraGestureOptions>;
  private canvas: HTMLCanvasElement | null = null;
  private isAttached = false;
  
  // Camera state (alpha, beta, radius)
  private _state: CameraState = {
    alpha: Math.PI / 4,
    beta: Math.PI / 2,
    radius: 5,
    baseRadius: 5,
  };
  
  // Velocity for inertia
  private velocity: VelocityState = {
    alpha: 0,
    beta: 0,
    targetAlpha: 0,
    targetBeta: 0,
  };
  
  // Touch/pointer state
  private isDragging = false;
  private hasRotationGesture = false;
  private lastX = 0;
  private lastY = 0;
  private lastPinchDistance = 0;
  
  // Page scroll passthrough state
  private touchAxisPending = false;
  private touchIgnoreUntilEnd = false;
  private touchStartX = 0;
  private touchStartY = 0;
  private static readonly TOUCH_AXIS_LOCK_THRESHOLD_PX = 10;
  
  // Bound handlers for cleanup
  private boundPointerDown: ((e: PointerEvent) => void) | null = null;
  private boundPointerMove: ((e: PointerEvent) => void) | null = null;
  private boundPointerUp: ((e: PointerEvent) => void) | null = null;
  private boundTouchStart: ((e: TouchEvent) => void) | null = null;
  private boundTouchMove: ((e: TouchEvent) => void) | null = null;
  private boundTouchEnd: ((e: TouchEvent) => void) | null = null;
  private boundWheel: ((e: WheelEvent) => void) | null = null;
  
  constructor(options?: CameraGestureOptions) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }
  
  // ============================================================================
  // Public API
  // ============================================================================
  
  /**
   * Get current camera state (read-only access)
   */
  public get state(): Readonly<CameraState> {
    return this._state;
  }
  
  /**
   * Check if currently dragging
   */
  public get dragging(): boolean {
    return this.isDragging;
  }
  
  /**
   * Check if attached to a canvas
   */
  public get attached(): boolean {
    return this.isAttached;
  }
  
  /**
   * Set camera state (e.g., after framing a model)
   */
  public setState(state: Partial<CameraState>): void {
    if (state.alpha !== undefined) this._state.alpha = state.alpha;
    if (state.beta !== undefined) this._state.beta = state.beta;
    if (state.radius !== undefined) this._state.radius = state.radius;
    if (state.baseRadius !== undefined) this._state.baseRadius = state.baseRadius;
  }
  
  /**
   * Reset velocity and dragging state
   */
  public resetVelocity(): void {
    this.isDragging = false;
    this.hasRotationGesture = false;
    this.velocity.alpha = 0;
    this.velocity.beta = 0;
    this.velocity.targetAlpha = 0;
    this.velocity.targetBeta = 0;
    this.lastPinchDistance = 0;
    this.touchAxisPending = false;
    this.touchIgnoreUntilEnd = false;
  }
  
  /**
   * Update options
   */
  public setOptions(options: Partial<CameraGestureOptions>): void {
    this.options = { ...this.options, ...options };
    if (!this.canRotateVertical()) {
      this.velocity.beta = 0;
      this.velocity.targetBeta = 0;
    }
  }
  
  /**
   * Attach to a canvas element
   */
  public attach(canvas: HTMLCanvasElement): void {
    if (this.isAttached) {
      this.detach();
    }
    
    this.canvas = canvas;
    
    // Create bound handlers
    this.boundPointerDown = this.onPointerDown.bind(this);
    this.boundPointerMove = this.onPointerMove.bind(this);
    this.boundPointerUp = this.onPointerUp.bind(this);
    this.boundTouchStart = this.onTouchStart.bind(this);
    this.boundTouchMove = this.onTouchMove.bind(this);
    this.boundTouchEnd = this.onTouchEnd.bind(this);
    this.boundWheel = this.onWheel.bind(this);
    
    // Attach pointer events (for mouse)
    canvas.addEventListener('pointerdown', this.boundPointerDown, { passive: false });
    canvas.addEventListener('pointermove', this.boundPointerMove, { passive: false });
    canvas.addEventListener('pointerup', this.boundPointerUp);
    canvas.addEventListener('pointerleave', this.boundPointerUp);
    
    // Attach touch events (for touch devices)
    canvas.addEventListener('touchstart', this.boundTouchStart, { passive: false });
    canvas.addEventListener('touchmove', this.boundTouchMove, { passive: false });
    canvas.addEventListener('touchend', this.boundTouchEnd);
    canvas.addEventListener('touchcancel', this.boundTouchEnd);
    
    // Attach wheel event (for zoom)
    canvas.addEventListener('wheel', this.boundWheel, { passive: false });
    
    this.isAttached = true;
  }
  
  /**
   * Detach from canvas
   */
  public detach(): void {
    if (!this.isAttached || !this.canvas) return;
    
    // Remove all event listeners
    if (this.boundPointerDown) {
      this.canvas.removeEventListener('pointerdown', this.boundPointerDown);
      this.canvas.removeEventListener('pointermove', this.boundPointerMove!);
      this.canvas.removeEventListener('pointerup', this.boundPointerUp!);
      this.canvas.removeEventListener('pointerleave', this.boundPointerUp!);
    }
    
    if (this.boundTouchStart) {
      this.canvas.removeEventListener('touchstart', this.boundTouchStart);
      this.canvas.removeEventListener('touchmove', this.boundTouchMove!);
      this.canvas.removeEventListener('touchend', this.boundTouchEnd!);
      this.canvas.removeEventListener('touchcancel', this.boundTouchEnd!);
    }
    
    if (this.boundWheel) {
      this.canvas.removeEventListener('wheel', this.boundWheel);
    }
    
    // Clear references
    this.boundPointerDown = null;
    this.boundPointerMove = null;
    this.boundPointerUp = null;
    this.boundTouchStart = null;
    this.boundTouchMove = null;
    this.boundTouchEnd = null;
    this.boundWheel = null;
    this.canvas = null;
    
    this.resetVelocity();
    this.isAttached = false;
  }
  
  /**
   * Update inertia - call this in your render loop
   * Returns true if state changed (for re-render optimization)
   */
  public update(): boolean {
    if (this.options.disabled) return false;
    
    const { velocityEase, velocityDecay, velocityThreshold, minBeta, maxBeta } = this.options;
    let stateChanged = false;
    
    // Apply velocity easing
    if (this.isDragging) {
      this.velocity.alpha += (this.velocity.targetAlpha - this.velocity.alpha) * velocityEase;
      this.velocity.beta += (this.velocity.targetBeta - this.velocity.beta) * velocityEase;
      this.velocity.targetAlpha = 0;
      this.velocity.targetBeta = 0;
    } else {
      // Apply decay when not dragging
      this.velocity.alpha *= velocityDecay;
      this.velocity.beta *= velocityDecay;
      if (Math.abs(this.velocity.alpha) < velocityThreshold) this.velocity.alpha = 0;
      if (Math.abs(this.velocity.beta) < velocityThreshold) this.velocity.beta = 0;
    }
    
    // Apply velocity to state
    if (this.velocity.alpha !== 0 || this.velocity.beta !== 0) {
      this._state.alpha += this.velocity.alpha;
      this._state.beta += this.velocity.beta;
      
      // Clamp beta
      this._state.beta = Math.max(minBeta, Math.min(maxBeta, this._state.beta));
      
      stateChanged = true;
    }
    
    return stateChanged;
  }

  /**
   * Returns true only after a real rotation movement happened since last check.
   * Calling this consumes/reset the flag.
   */
  public consumeRotationGesture(): boolean {
    const rotated = this.hasRotationGesture;
    this.hasRotationGesture = false;
    return rotated;
  }
  
  /**
   * Dispose of resources
   */
  public dispose(): void {
    this.detach();
  }
  
  // ============================================================================
  // Private: Rotation helpers
  // ============================================================================
  
  private canRotateHorizontal(): boolean {
    const mode = this.options.rotation;
    return mode === 'horizontal' || mode === 'both';
  }
  
  private canRotateVertical(): boolean {
    const mode = this.options.rotation;
    return mode === 'vertical' || mode === 'both';
  }
  
  private canRotate(): boolean {
    return this.options.rotation !== 'none';
  }
  
  // ============================================================================
  // Private: Pointer Events (mouse)
  // ============================================================================
  
  private onPointerDown(e: PointerEvent): void {
    if (this.options.disabled) return;
    if (e.pointerType === 'touch') return; // Handle touch separately
    if (!this.canRotate()) return;
    
    this.startDrag(e.clientX, e.clientY);
    e.preventDefault();
  }
  
  private onPointerMove(e: PointerEvent): void {
    if (this.options.disabled) return;
    if (e.pointerType === 'touch') return;
    
    this.updateDrag(e.clientX, e.clientY);
    if (this.isDragging) e.preventDefault();
  }
  
  private onPointerUp(e: PointerEvent): void {
    if (this.options.disabled) return;
    if (e.pointerType === 'touch') return;
    
    this.endDrag();
  }
  
  // ============================================================================
  // Private: Touch Events
  // ============================================================================
  
  private onTouchStart(e: TouchEvent): void {
    if (this.options.disabled) return;
    
    if (e.touches.length === 1) {
      if (!this.canRotate()) return;
      
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      
      if (this.options.allowPageScroll) {
        // Wait for gesture direction before deciding to capture
        this.isDragging = false;
        this.touchAxisPending = true;
        this.touchIgnoreUntilEnd = false;
        this.touchStartX = x;
        this.touchStartY = y;
        this.lastX = x;
        this.lastY = y;
        return;
      }
      
      this.startDrag(x, y);
      e.preventDefault();
    } else if (e.touches.length === 2) {
      this.isDragging = false;
      this.touchAxisPending = false;
      this.touchIgnoreUntilEnd = false;
      this.lastPinchDistance = this.getPinchDistance(e);
      if (this.options.zoom) {
        e.preventDefault();
      }
    }
  }
  
  private onTouchMove(e: TouchEvent): void {
    if (this.options.disabled) return;
    let shouldConsume = false;

    if (e.touches.length === 1) {
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;

      if (this.options.allowPageScroll && this.touchAxisPending) {
        const totalDx = x - this.touchStartX;
        const totalDy = y - this.touchStartY;
        const absDx = Math.abs(totalDx);
        const absDy = Math.abs(totalDy);
        const movedEnough = absDx >= CameraGestureController.TOUCH_AXIS_LOCK_THRESHOLD_PX
          || absDy >= CameraGestureController.TOUCH_AXIS_LOCK_THRESHOLD_PX;

        if (movedEnough) {
          this.touchAxisPending = false;
          if (absDx > absDy && this.canRotateHorizontal()) {
            // Horizontal swipe - capture for rotation
            this.startDrag(x, y);
          } else {
            // Vertical swipe or can't rotate horizontal - let page scroll
            this.touchIgnoreUntilEnd = true;
          }
        }
      }

      if (!this.touchIgnoreUntilEnd && this.isDragging) {
        this.updateDrag(x, y);
        shouldConsume = true;
      }
    } else if (e.touches.length === 2) {
      this.touchAxisPending = false;
      this.touchIgnoreUntilEnd = false;
      if (this.options.zoom) {
        this.handlePinchZoom(e);
        shouldConsume = true;
      }
    }

    if (shouldConsume) {
      e.preventDefault();
    }
  }
  
  private onTouchEnd(e: TouchEvent): void {
    if (this.options.disabled) return;
    
    if (e.touches.length === 0) {
      this.endDrag();
      this.touchAxisPending = false;
      this.touchIgnoreUntilEnd = false;
    }
    this.lastPinchDistance = 0;
  }
  
  // ============================================================================
  // Private: Wheel Event (zoom)
  // ============================================================================
  
  private onWheel(e: WheelEvent): void {
    if (this.options.disabled) return;
    if (!this.options.zoom) return;
    
    const { minZoom, maxZoom } = this.options;
    const zoomFactor = 1 + e.deltaY * 0.001;
    
    this._state.radius *= zoomFactor;
    this._state.radius = Math.max(
      this._state.baseRadius * minZoom,
      Math.min(this._state.baseRadius * maxZoom, this._state.radius)
    );
    
    e.preventDefault();
  }
  
  // ============================================================================
  // Private: Drag Helpers
  // ============================================================================
  
  private startDrag(x: number, y: number): void {
    this.isDragging = true;
    this.lastX = x;
    this.lastY = y;
    this.velocity.targetAlpha = 0;
    this.velocity.targetBeta = 0;
  }
  
  private updateDrag(x: number, y: number): void {
    if (!this.isDragging) return;
    
    const deltaX = x - this.lastX;
    const deltaY = y - this.lastY;
    
    this.velocity.targetAlpha = this.canRotateHorizontal()
      ? deltaX * this.options.rotationSpeed
      : 0;
    this.velocity.targetBeta = this.canRotateVertical()
      ? -deltaY * this.options.rotationSpeed
      : 0;

    if (this.velocity.targetAlpha !== 0 || this.velocity.targetBeta !== 0) {
      this.hasRotationGesture = true;
    }
    
    this.lastX = x;
    this.lastY = y;
  }
  
  private endDrag(): void {
    this.isDragging = false;
  }
  
  // ============================================================================
  // Private: Pinch Helpers
  // ============================================================================
  
  private getPinchDistance(e: TouchEvent): number {
    if (e.touches.length < 2) return 0;
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
  
  private handlePinchZoom(e: TouchEvent): void {
    if (!this.options.zoom) return;
    const pinchDistance = this.getPinchDistance(e);
    
    if (this.lastPinchDistance > 0) {
      const { minZoom, maxZoom } = this.options;
      const scale = this.lastPinchDistance / pinchDistance;
      
      this._state.radius *= scale;
      this._state.radius = Math.max(
        this._state.baseRadius * minZoom,
        Math.min(this._state.baseRadius * maxZoom, this._state.radius)
      );
    }
    
    this.lastPinchDistance = pinchDistance;
  }
}
